import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { ArrowRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

import kentImage from '@/assets/kent-headshot-2025.jpg';
import heroAboutBg from '@/assets/hero-about-coaching.jpg';
import textureWash from '@/assets/leaders-texture-wash.jpg';
import dougTurnerImage from '@/assets/doug-turner.png';

const hallmarks = [
  { artificial: 'Manages impression', human: 'Leads with presence' },
  { artificial: 'Answers from certainty', human: 'Sits with not knowing' },
  { artificial: 'Avoids discomfort', human: 'Moves toward difficulty' },
  { artificial: 'Performs confidence', human: 'Owns vulnerability' },
  { artificial: 'Drives through control', human: 'Moves through trust' },
  { artificial: 'Leads from the head', human: 'Leads from the whole' },
  { artificial: 'Optimizes for output', human: 'Cultivates meaning' },
];

const challenges = [
  'The playbook that built your career is quietly expiring.',
  'You have run out of frameworks that fit the moment.',
  'AI is reshaping the work faster than your organization can absorb it.',
  'You can see what is coming, but the system around you cannot yet.',
  'You fear failure, even as you succeed.',
  'You are carrying weight you cannot put down.',
];

// ============ Hero — combined opening with practice statement ============
const AboutHero = () => {
  const lead = useScrollReveal<HTMLDivElement>();
  const tail = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative pt-56 pb-24 sm:pt-44 sm:pb-32 overflow-hidden bg-foreground">
      <div className="absolute inset-0">
        <img
          src={heroAboutBg}
          alt=""
          className="w-full h-full object-cover object-center opacity-35"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 bg-foreground/75" />
      <div
        className="absolute inset-0 pointer-events-none opacity-80"
        style={{
          background:
            'radial-gradient(ellipse at 15% 80%, hsl(var(--primary) / 0.22) 0%, transparent 55%), radial-gradient(ellipse at 90% 10%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-24 md:gap-16 items-end">
          {/* Lead column — identity */}
          <div
            ref={lead.ref}
            className={`md:col-span-7 transition-all duration-1000 ${
              lead.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-primary" aria-hidden="true" />
              <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                About ParadoxEdge
              </p>
            </div>
            <h1
              className="text-background text-5xl sm:text-6xl lg:text-[5.5rem] leading-[1.02] mb-10"
              style={{ textWrap: 'balance' }}
            >
              We develop leaders who <span className="text-primary italic">meet the moment.</span>
            </h1>
            <p className="text-background/80 text-lg sm:text-xl leading-relaxed font-light whitespace-nowrap">
              More views. New ways. Inspired outcomes.
            </p>
          </div>

          {/* Tail column — practice context */}
          <div
            ref={tail.ref}
            className={`md:col-span-5 md:pl-8 md:border-l md:border-background/15 transition-all duration-1000 delay-200 ${
              tail.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs mb-5">
              Our Practice
            </p>
            <p className="text-background/80 text-lg leading-relaxed font-light">
              We work with senior leaders and their teams in sustained, confidential engagements. Every practice is built around the person in front of us. The work moves through relationship.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ Hallmarks — dark editorial spread with contrast frame ============
const HallmarkRow = ({
  item,
  index,
}: {
  item: { artificial: string; human: string };
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${index * 70}ms` : '0ms' }}
      className={`group relative grid grid-cols-[1fr_auto_1fr] items-center gap-4 sm:gap-8 px-5 sm:px-8 py-6 sm:py-8 rounded-sm
        bg-background/[0.03] border border-background/10
        hover:border-primary/40 hover:bg-background/[0.06]
        transition-all duration-700
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <p className="text-background/45 text-base sm:text-lg leading-snug line-through decoration-background/20">
        {item.artificial}
      </p>

      <div className="flex items-center gap-2 text-primary" aria-hidden="true">
        <span className="hidden sm:block h-px w-6 bg-primary/50" />
        <ArrowRight className="h-4 w-4" />
      </div>

      <p className="text-background font-display text-lg sm:text-2xl leading-snug group-hover:text-primary transition-colors duration-500">
        {item.human}
      </p>
    </article>
  );
};

const Hallmarks = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.18) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-5xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Hallmarks of Leadership
            </p>
          </div>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6"
            style={{ textWrap: 'balance' }}
          >
            From artificial performance to <span className="text-primary italic">fully human presence.</span>
          </h2>
          <p className="text-background/70 text-xl leading-relaxed font-light max-w-2xl">
            The shift our clients move through. Visible in their meetings, decisions, and relationships.
          </p>
        </div>

        {/* Column labels */}
        <div className="grid grid-cols-[auto_1fr_auto_1fr] items-center gap-4 sm:gap-8 px-5 sm:px-8 mb-4">
          <span className="w-8" aria-hidden="true" />
          <p className="text-background/40 font-semibold tracking-[0.2em] uppercase text-[0.7rem]">
            Artificial
          </p>
          <span aria-hidden="true" />
          <p className="text-primary font-semibold tracking-[0.2em] uppercase text-[0.7rem]">
            Fully Human
          </p>
        </div>

        <div className="space-y-3">
          {hallmarks.map((item, i) => (
            <HallmarkRow key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Your Guide — Kent ============
const YourGuide = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const photo = useScrollReveal<HTMLDivElement>();
  const body = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative bg-secondary/40 py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 20% 15%, hsl(var(--primary) / 0.08) 0%, transparent 55%), radial-gradient(ellipse at 85% 85%, hsl(var(--sage) / 0.08) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Your Guide
            </p>
          </div>
          <h2
            className="text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            Kent H. <span className="text-primary italic">Frazier</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div
            ref={photo.ref}
            className={`md:col-span-5 md:sticky md:top-32 transition-all duration-1000 ${
              photo.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <figure className="relative">
              {/* Vertical accent rule running alongside the photo */}
              <span
                className="hidden sm:block absolute -left-6 top-6 bottom-6 w-px bg-primary"
                aria-hidden="true"
              />
              {/* Portrait */}
              <div className="relative aspect-[4/5] overflow-hidden bg-secondary max-w-[220px] sm:max-w-[280px] md:max-w-[320px] mx-auto md:mx-0">
                <img
                  src={kentImage}
                  alt="Kent H. Frazier — Co-Founder and CEO, ParadoxEdge"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
              {/* Caption — editorial, no chrome */}
              <figcaption className="mt-6 flex items-baseline gap-4">
                <p className="text-primary font-semibold tracking-[0.25em] uppercase text-[0.7rem] whitespace-nowrap">
                  Co-Founder & CEO
                </p>
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
                <p className="text-foreground/60 text-xs italic whitespace-nowrap">
                  Denver, CO
                </p>
              </figcaption>
            </figure>
          </div>

          <div
            ref={body.ref}
            className={`md:col-span-7 space-y-7 text-lg sm:text-xl text-foreground/80 leading-relaxed font-light transition-all duration-1000 delay-200 ${
              body.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p>
              Kent has spent three decades inside the work of leadership. Former HR executive, Certified Integral Master Coach, lifelong practitioner.
            </p>
            <blockquote className="border-l-2 border-primary pl-6 py-2 font-display italic text-foreground text-2xl sm:text-3xl leading-snug">
              Evoking the human spirit. Creating the conditions where leaders reconnect with their innate wholeness, creativity, and generative power as an ongoing practice.
            </blockquote>
            <p>
              Kent co-founded ParadoxEdge in 2011 with Steve Benson (retired) and Reggie Marra (focused on other projects). Since then, he has partnered with leaders at Amazon Web Services, DaVita, Ghirardelli, PETCO, and many others. He leads every engagement personally, and draws on a trusted bench of coaches, facilitators, and practitioners. He also coaches with Stagen Leadership Academy, Workbench, and Abundance Labs.
            </p>
            <p>
              He is a devoted father, musician, athlete, and author. His most fundamental teaching? Relax.
            </p>
            <p className="text-foreground/60 text-sm leading-relaxed pt-2 border-t border-border/60">
              Certified Integral Master Coach · Certified Strengthscope Practitioner · Author of Love Fiercely: The Way of The Wood Dragon
            </p>
            <a
              href="https://kenthfrazier.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary font-semibold text-base hover:gap-3 transition-all"
            >
              Learn more about Kent
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ Featured Testimonial ============
const FeaturedTestimonial = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src={textureWash} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 80% 20%, hsl(var(--primary) / 0.14) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-4xl mx-auto transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <Quote className="h-12 w-12 text-primary mb-8" />
        <blockquote className="space-y-8">
          <p className="text-2xl sm:text-3xl lg:text-4xl text-background leading-snug font-display italic" style={{ textWrap: 'balance' }}>
            Kent and his team have worked with me and my team for almost three years. I am hard pressed to point to an initiative that has had a more significant impact on how we feel about each other and how we engage as a team.
          </p>
          <footer className="flex items-center gap-4 pt-4">
            <img
              src={dougTurnerImage}
              alt="Doug Turner"
              loading="lazy"
              className="w-14 h-14 rounded-full object-cover"
            />
            <div>
              <cite className="not-italic font-semibold text-primary text-lg block">Doug Turner</cite>
              <p className="text-background/60 text-sm">Director, Amazon Web Services Global Services</p>
            </div>
          </footer>
        </blockquote>
      </div>
    </section>
  );
};

// ============ Who We Work With — editorial recognition list ============
const ChallengeCard = ({
  text,
  index,
}: {
  text: string;
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${(index % 3) * 90}ms` : '0ms' }}
      className={`group relative bg-background border border-foreground/10 rounded-sm p-7 sm:p-9
        transition-all duration-700
        hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <div className="w-10 h-px bg-primary mb-5 group-hover:w-16 transition-[width] duration-500" aria-hidden="true" />
      <p className="text-foreground font-display text-lg sm:text-xl leading-snug group-hover:text-primary transition-colors duration-500">
        {text}
      </p>
    </article>
  );
};

const WhoWeWorkWith = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative py-24 sm:py-36 bg-secondary/60 overflow-hidden">
      <div
        className="absolute inset-0 opacity-80 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.14) 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, hsl(var(--primary) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-12 sm:mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-6 sm:mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              Who We Work With
            </p>
          </div>
          <h2
            className="text-foreground text-3xl sm:text-5xl lg:text-6xl leading-[1.1] mb-5"
            style={{ textWrap: 'balance' }}
          >
            You are standing at the <span className="text-primary italic">edge of something.</span>
          </h2>
          <p className="text-foreground/70 text-lg sm:text-xl leading-relaxed font-light">
            If any of these resonate, read on.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {challenges.map((challenge, i) => (
            <ChallengeCard key={i} text={challenge} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Results — dark editorial proof band ============
const Results = () => {
  const heading = useScrollReveal<HTMLDivElement>();
  const stats = useScrollReveal<HTMLDivElement>();

  const items = [
    { value: '↑ 26%', label: 'Thriving at Work', context: 'Increase in self-reported flourishing across cohorts.' },
    { value: '91', label: 'Net Promoter Score', context: 'How likely past participants are to recommend the work.' },
    { value: '↓ 40%', label: 'Work-Related Stress', context: 'Reduction in chronic stress reported six months in.' },
  ];

  return (
    <section className="relative bg-foreground text-background py-28 sm:py-40 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 85% 15%, hsl(var(--primary) / 0.18) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-6xl mx-auto">
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-12 sm:mb-16 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-6 sm:mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              The Proof
            </p>
          </div>
          <h2
            className="text-background text-3xl sm:text-5xl lg:text-6xl leading-[1.1] mb-5"
            style={{ textWrap: 'balance' }}
          >
            What changes when leaders <span className="text-primary italic">do this work.</span>
          </h2>
          <p className="text-background/70 text-lg sm:text-xl leading-relaxed font-light max-w-2xl">
            Outcomes reported by participants across multi-year engagements.
          </p>
        </div>

        <div
          ref={stats.ref}
          className={`grid sm:grid-cols-3 sm:divide-x sm:divide-background/15 transition-all duration-1000 ${
            stats.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {items.map((item, i) => (
            <div
              key={i}
              className="relative py-10 sm:py-4 sm:px-8 lg:px-10 first:sm:pl-0 last:sm:pr-0 border-b sm:border-b-0 border-background/15 last:border-b-0"
            >
              <div className="w-10 h-px bg-primary mb-5" aria-hidden="true" />
              <div className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary mb-4 leading-none">
                {item.value}
              </div>
              <p className="text-background font-display text-xl mb-2">
                {item.label}
              </p>
              <p className="text-background/60 text-sm leading-relaxed font-light">
                {item.context}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ Final CTA ============
const FinalCTA = () => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  const handleScheduleCall = () => {
    window.open('https://calendly.com/kentfrazier/30-minutes', '_blank');
  };
  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, hsl(var(--primary) / 0.18) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />
      <div
        ref={ref}
        className={`container-mobile relative z-10 max-w-3xl mx-auto text-center space-y-10 transition-all duration-1000 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <p className="text-primary font-display text-xl sm:text-2xl italic">
          Organizations do not change. People do.
        </p>
        <h2
          className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          style={{ textWrap: 'balance' }}
        >
          What <span className="text-primary italic">now?</span>
        </h2>
        <Button
          onClick={handleScheduleCall}
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg px-10 py-7 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 hover:scale-105 group"
        >
          Begin a Conversation
          <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </section>
  );
};

const About = () => {
  const aboutJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About ParadoxEdge",
      "url": "https://www.paradoxedge.com/about",
      "description": "ParadoxEdge is a Denver-based executive coaching practice founded by Kent Frazier. We catalyze development for leaders and organizations through continuous practice.",
      "mainEntity": {
        "@type": "Person",
        "name": "Kent Frazier",
        "jobTitle": "Founder & CEO, ParadoxEdge",
        "url": "https://www.linkedin.com/in/kent-frazier/",
        "worksFor": { "@type": "Organization", "name": "ParadoxEdge" }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Kent H. Frazier",
      "givenName": "Kent",
      "familyName": "Frazier",
      "jobTitle": "Founder & CEO",
      "description": "Executive coach and former HR executive. Three decades inside the work of leadership, partnering with senior leaders at Amazon Web Services, DaVita, Ghirardelli, PETCO, and others.",
      "url": "https://www.paradoxedge.com/about",
      "image": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      "worksFor": {
        "@type": "Organization",
        "name": "ParadoxEdge",
        "url": "https://www.paradoxedge.com"
      },
      "hasCredential": [
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Certified Integral Master Coach" },
        { "@type": "EducationalOccupationalCredential", "credentialCategory": "certification", "name": "Certified Strengthscope Practitioner" }
      ],
      "knowsAbout": [
        "Executive Coaching",
        "Leadership Development",
        "Vertical Development",
        "Organizational Development",
        "Human and AI Leadership",
        "Presence-Based Coaching"
      ],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Denver",
        "addressRegion": "CO",
        "addressCountry": "US"
      },
      "sameAs": [
        "https://www.linkedin.com/in/kent-frazier/",
        "https://kenthfrazier.com"
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.paradoxedge.com/" },
        { "@type": "ListItem", "position": 2, "name": "About", "item": "https://www.paradoxedge.com/about" }
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="About Kent Frazier & ParadoxEdge"
        description="Meet Kent Frazier and ParadoxEdge — a Denver-based executive coaching practice catalyzing development for leaders through continuous practice."
        canonicalUrl="/about"
        keywords="about ParadoxEdge, Kent Frazier executive coach, leadership coaching company, coaching philosophy, Denver executive coach"
        jsonLd={aboutJsonLd}
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <ElfsightWidget />
        <main>
          <AboutHero />
          
          <Hallmarks />
          <YourGuide />
          <FeaturedTestimonial />
          
          <WhoWeWorkWith />
          <Results />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default About;
